var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { FormField, FormGrid } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo } from 'react';
import { usePreviousDistinct } from 'react-use';
import { ApiErrorStatusCode } from 'api/api';
import { customFieldsApi, } from 'apis/customFields';
import { analytics } from 'app/utils/segment';
import { actions as conversationsActions } from 'components/conversation/actions/conversation';
import { isQueryError, isResponseWithData } from 'utils/apis/axios';
import { getEmptyCustomFields } from 'utils/customFields';
import { useCustomFieldsVisibility } from 'utils/customFields/useCustomFieldsVisibility';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCurrentUserInAcw } from 'utils/user';
import { CustomFieldText } from './components/fieldText/customFieldText';
import { CustomFieldSelect } from './components/select/customFieldSelect';
/** Renders a form containing custom fields of a conversation. */
export const ConversationCustomFieldsForm = ({ csid, customFields, }) => {
    var _a;
    const queue = useAppSelector((state) => state.conversation.metaConversation.conversation.queue);
    const visibility = useCustomFieldsVisibility(customFields, {
        queue: [(_a = queue === null || queue === void 0 ? void 0 : queue.id) !== null && _a !== void 0 ? _a : ''],
    });
    const dispatch = useAppDispatch();
    const { requiredCustomAttribute, dependantCustomAttribute } = useFeatureFlag([
        'required-custom-attribute',
        'dependant-custom-attribute',
    ]);
    const isAcw = useCurrentUserInAcw({ csid });
    const isAssignedToMe = useAppSelector((state) => state.conversation.metaConversation.isAssignedToMe);
    const hasEmptyRequiredAttributes = useMemo(() => getEmptyCustomFields({ customFields, onlyRequired: true }).length > 0, [customFields]);
    const selectReadValue = useMemo(() => customFieldsApi.endpoints.readValue.select({
        entityId: 'conversation',
        entityInstanceId: csid,
    }), [csid]);
    const { error: readValueError } = useAppSelector(selectReadValue);
    const shouldUpsertQueryData = isQueryError(readValueError) &&
        readValueError.status === ApiErrorStatusCode.NotFound;
    // Need to track `csid` changes in addition to `hasEmptyRequiredAttributes`
    // because navigating through conversations, some conversations may
    // have missing required attributes and some may not. This causes
    // `hasEmptyRequiredAttributes` change. So, we need to track the previous
    // value of `csid` and `hasEmptyRequiredAttributes` combined. Then we can
    // check if `hasEmptyRequiredAttributes` of the same conversation is changed.
    const current = useMemo(() => ({
        csid,
        hasEmptyRequiredAttributes,
    }), [csid, hasEmptyRequiredAttributes]);
    const previous = usePreviousDistinct(current);
    useEffect(() => {
        if (requiredCustomAttribute &&
            isAcw &&
            isAssignedToMe &&
            (previous === null || previous === void 0 ? void 0 : previous.csid) === current.csid &&
            previous.hasEmptyRequiredAttributes &&
            !current.hasEmptyRequiredAttributes) {
            dispatch(conversationsActions.conversationUnblockAcw({ csid }));
        }
    }, [
        csid,
        current,
        dispatch,
        isAcw,
        isAssignedToMe,
        previous,
        requiredCustomAttribute,
    ]);
    const [updateValue] = customFieldsApi.useUpdateValueMutation();
    const save = useCallback((attribute, patch) => __awaiter(void 0, void 0, void 0, function* () {
        const { id: attributeId, entity_id: entityId, type } = attribute;
        const response = yield updateValue({
            entityId,
            entityInstanceId: csid,
            patch,
        });
        if (isResponseWithData(response)) {
            if (shouldUpsertQueryData) {
                // We do optimistic updates on save. However, if there is already no
                // value saved for the current conversation, the readValue api will
                // return 404 response and the optimistic update will not work. Hence,
                // we have to manually update the cache after saving the first value
                // in order for the next optimistic updates to work.
                dispatch(customFieldsApi.util.upsertQueryData('readValue', { entityId, entityInstanceId: csid }, response.data));
            }
            analytics.customAttributeValueUpdated({
                entityId,
                entityInstanceId: `${csid}`,
                attributeId,
                type,
            });
            return response.data;
        }
        return Promise.reject(response.error);
    }), [csid, dispatch, shouldUpsertQueryData, updateValue]);
    const renderField = (customField) => {
        if (customField.attribute.type === 'text') {
            return (_jsx(CustomFieldText, { isAcw: isAcw, customField: customField, save: save }));
        }
        if (customField.attribute.type === 'select') {
            return (_jsx(CustomFieldSelect, { isAcw: isAcw, customField: customField, save: save }));
        }
    };
    return (_jsx(FormGrid, { children: customFields.map((customField) => {
            const { attribute } = customField;
            if (dependantCustomAttribute && !visibility[attribute.id]) {
                return null;
            }
            return (_jsx(FormField, { children: renderField(customField) }, attribute.id));
        }) }));
};
